import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { shareDocument, listShares, revokeShare, recentRecipients } from "../../api/shareApi";
import "./shareModal.css";

/**
 * Point a colleague at this document.
 *
 * <b>Sharing gives nobody access.</b> It puts a note in someone's list and rings their bell; when they open
 * the document they pass exactly the checks they always would. The dialog says so out loud rather than
 * leaving people to assume otherwise, because "I shared it with you" sounding like "I gave you access" is
 * how a governed archive quietly stops being governed.
 *
 * <b>No directory lookup.</b> The staff directory is admin-gated by design, and this is an ordinary user's
 * screen, so recipients are typed as account names and the suggestions are people the caller has shared
 * with before — their own data, disclosing nothing new.
 *
 * The server answers per recipient, and all three answers are surfaced: shared, already shared, notified or
 * not (no account by that name), and whether the person will actually be allowed to open it.
 *
 * Props: open, dbName, archiveNo, token, onClose
 */
export default function ShareModal({ open, dbName, archiveNo, token, onClose }) {
  const { t } = useTranslation();

  const [draft, setDraft] = useState("");
  const [chips, setChips] = useState([]);
  const [message, setMessage] = useState("");
  const [expires, setExpires] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [existing, setExisting] = useState([]);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!open || !token || !dbName || !archiveNo) return;
    setError("");
    try {
      const [recent, current] = await Promise.all([
        recentRecipients(token).catch(() => ({ recipients: [] })),
        listShares(token, dbName, archiveNo),
      ]);
      setSuggestions(recent?.recipients || []);
      setExisting(current?.shares || []);
    } catch (e) {
      setError(e?.status === 403
        ? t("share.notLicensed", { defaultValue: "Sharing is part of content governance, which is not enabled." })
        : (e?.message || t("share.loadFailed", { defaultValue: "The sharing details could not be loaded." })));
    }
  }, [open, token, dbName, archiveNo, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!open) { setDraft(""); setChips([]); setMessage(""); setExpires(""); setResults(null); setError(""); }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const addChip = (raw) => {
    const name = (raw || "").trim();
    if (!name) return;
    if (chips.some((c) => c.toLowerCase() === name.toLowerCase())) { setDraft(""); return; }
    setChips([...chips, name]);
    setDraft("");
  };

  const onDraftKey = (e) => {
    // Enter, comma and semicolon all commit a name: people paste lists separated by any of the three.
    if (e.key === "Enter" || e.key === "," || e.key === ";") { e.preventDefault(); addChip(draft); }
    else if (e.key === "Backspace" && !draft && chips.length) setChips(chips.slice(0, -1));
  };

  const submit = async () => {
    const all = draft.trim() ? [...chips, draft.trim()] : chips;
    if (!all.length || busy) return;
    setBusy(true);
    setError("");
    try {
      const res = await shareDocument(token, {
        databaseName: dbName,
        archiveNo,
        recipients: all,
        message: message.trim() || undefined,
        // A date input gives local midnight; the server stores UTC and compares in UTC.
        expiresAt: expires ? new Date(`${expires}T23:59:59`).toISOString() : undefined,
      });
      setResults(res?.results || []);
      setChips([]);
      setDraft("");
      await load();
    } catch (e) {
      setError(e?.code === "TOO_MANY_RECIPIENTS"
        ? t("share.tooMany", { defaultValue: "At most 25 people at a time." })
        : e?.code === "NO_RECIPIENTS"
          ? t("share.noRecipients", { defaultValue: "Name at least one colleague other than yourself." })
          : (e?.message || t("share.failed", { defaultValue: "The document could not be shared." })));
    } finally { setBusy(false); }
  };

  const withdraw = async (id) => {
    setBusy(true);
    try {
      await revokeShare(token, id, dbName);
      await load();
    } catch (e) {
      setError(e?.message || t("share.revokeFailed", { defaultValue: "The share could not be withdrawn." }));
    } finally { setBusy(false); }
  };

  const when = (iso) => { try { return new Date(iso).toLocaleDateString(); } catch { return ""; } };

  if (!open) return null;

  return createPortal(
    <div className="sh-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sh-modal" role="dialog" aria-modal="true">
        <div className="sh-head">
          <span className="sh-title">{t("share.title", { defaultValue: "Share with a colleague" })}</span>
          <button type="button" className="sh-x" onClick={onClose}>✕</button>
        </div>

        <div className="sh-note">
          {t("share.notAGrant", {
            defaultValue: "Sharing points someone at this document. It does not give them access — they still need to be entitled to the database it lives in.",
          })}
        </div>

        <div className="sh-body">
          <label className="sh-label" htmlFor="sh-people">
            {t("share.people", { defaultValue: "People" })}
          </label>
          <div className="sh-chips">
            {chips.map((c) => (
              <span className="sh-chip" key={c}>
                {c}
                <button type="button" onClick={() => setChips(chips.filter((x) => x !== c))} aria-label="remove">✕</button>
              </span>
            ))}
            <input
              id="sh-people"
              className="sh-chip-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onDraftKey}
              onBlur={() => addChip(draft)}
              placeholder={t("share.placeholder", { defaultValue: "Account name, then Enter" })}
            />
          </div>

          {suggestions.length > 0 && (
            <div className="sh-suggest">
              <span className="sh-suggest-label">{t("share.recent", { defaultValue: "Recent" })}</span>
              {suggestions.filter((s) => !chips.some((c) => c.toLowerCase() === s.toLowerCase())).map((s) => (
                <button type="button" key={s} className="sh-suggest-item" onClick={() => addChip(s)}>{s}</button>
              ))}
            </div>
          )}

          <label className="sh-label" htmlFor="sh-msg">{t("share.message", { defaultValue: "Message" })}</label>
          <textarea
            id="sh-msg"
            className="sh-textarea"
            maxLength={1000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t("share.messagePlaceholder", { defaultValue: "Why should they look at this? (optional)" })}
          />

          <label className="sh-label" htmlFor="sh-exp">
            {t("share.expires", { defaultValue: "Stop showing it after (optional)" })}
          </label>
          <input id="sh-exp" type="date" className="sh-date" value={expires} onChange={(e) => setExpires(e.target.value)} />

          {error && <div className="sh-error">{error}</div>}

          {results && results.length > 0 && (
            <div className="sh-results">
              {results.map((r) => (
                <div className="sh-result" key={r.userName}>
                  <strong>{r.userName}</strong>{" "}
                  {r.status === "alreadyShared"
                    ? t("share.alreadyShared", { defaultValue: "already had this" })
                    : t("share.shared", { defaultValue: "shared" })}
                  {r.status === "shared" && r.notified === false && (
                    <div className="sh-warn">
                      {t("share.notNotified", {
                        defaultValue: "No account by that name, so nobody was notified. The share is recorded.",
                      })}
                    </div>
                  )}
                  {r.recipientCanOpen === false && (
                    <div className="sh-warn">
                      {t("share.cannotOpen", {
                        defaultValue: "They are not currently entitled to this database, so they will not be able to open it.",
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {existing.length > 0 && (
            <div className="sh-existing">
              <div className="sh-label">{t("share.alreadyWith", { defaultValue: "Already shared with" })}</div>
              {existing.map((s) => (
                <div className="sh-row" key={s.id}>
                  <span className="sh-who">{s.sharedWithUserName}</span>
                  <span className="sh-meta">
                    {s.seen ? t("share.seen", { defaultValue: "opened" }) : t("share.unseen", { defaultValue: "not opened yet" })}
                    {s.expiresAt ? ` · ${t("share.until", { defaultValue: "until" })} ${when(s.expiresAt)}` : ""}
                  </span>
                  {s.canRevoke && (
                    <button type="button" className="sh-link danger" disabled={busy} onClick={() => withdraw(s.id)}>
                      {t("share.withdraw", { defaultValue: "Withdraw" })}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="sh-foot">
          <button type="button" className="sh-btn" onClick={onClose}>
            {t("common.close", { defaultValue: "Close" })}
          </button>
          <button
            type="button"
            className="sh-btn primary"
            disabled={busy || (!chips.length && !draft.trim())}
            onClick={submit}
          >
            {t("share.send", { defaultValue: "Share" })}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
